import { useEffect, useRef } from 'react';
import type { FC } from 'react';
import { Modal, Input, Empty, Tag, Typography } from 'antd';
import { SearchOutlined, PushpinFilled, PushpinOutlined } from '@ant-design/icons';
import type { InputRef } from 'antd/es/input';

import type { CommandPaletteItem, UseCommandPaletteReturn } from '../hooks/useCommandPalette.ts';

interface CommandPaletteProps {
  controller: UseCommandPaletteReturn;
}

const MAX_VISIBLE_RESULTS = 12;

const CommandPalette: FC<CommandPaletteProps> = ({ controller }) => {
  const {
    isOpen,
    close,
    query,
    setQuery,
    results,
    highlightedIndex,
    setHighlightedIndex,
    moveHighlight,
    selectItem,
  } = controller;

  const inputRef = useRef<InputRef>(null);
  const activeId = results[highlightedIndex]?.id;

  useEffect(() => {
    if (isOpen) {
      const timeout = window.setTimeout(() => {
        inputRef.current?.focus({ cursor: 'end' });
      }, 10);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveHighlight(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveHighlight(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const item = results[highlightedIndex];
      if (item) {
        selectItem(item);
      }
    }
  };

  const renderIcon = (item: CommandPaletteItem) => {
    if (item.type === 'action') {
      const Icon = item.relatedKey && item.title.startsWith('Quitar') ? PushpinFilled : PushpinOutlined;
      return <Icon />;
    }

    const Icon = item.icon;
    return <Icon />;
  };

  return (
    <Modal
      open={isOpen}
      onCancel={close}
      footer={null}
      title="Comandos del dashboard"
      width={560}
      destroyOnHidden
      mask={{ closable: true }}
      keyboard
      styles={{ body: { display: 'flex', flexDirection: 'column', gap: 12 } }}
      style={{ top: 80 }}
    >
      <div role="presentation" onKeyDown={handleKeyDown}>
        <Input
          ref={inputRef}
          size="large"
          prefix={<SearchOutlined />}
          placeholder="Buscar páginas o acciones..."
          aria-label="Buscar comandos del dashboard"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          data-testid="command-palette-input"
        />
      </div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Mostrando hasta {Math.min(results.length, MAX_VISIBLE_RESULTS)} resultados. Usá ↑ ↓ para navegar y Enter para ejecutar.
      </Typography.Text>
      {!results.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin resultados. Intentá con otro término o verificá tus permisos."
        />
      ) : (
        <ul
          role="listbox"
          aria-activedescendant={activeId}
          aria-label="Resultados del command palette"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            maxHeight: 360,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {results.map((item, index) => {
            const isActive = index === highlightedIndex;
            return (
              <li key={item.id} id={item.id} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => selectItem(item)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid var(--ant-color-border, rgba(0,0,0,0.06))',
                    borderRadius: 8,
                    background: isActive ? 'var(--ant-color-fill-secondary, rgba(0,0,0,0.04))' : 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  <span aria-hidden style={{ fontSize: 18, display: 'flex', alignItems: 'center' }}>
                    {renderIcon(item)}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <Typography.Text strong style={{ display: 'block' }}>
                      {item.title}
                    </Typography.Text>
                    {item.description ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {item.description}
                      </Typography.Text>
                    ) : null}
                  </span>
                  {item.type === 'navigation' ? (
                    <Tag color="default" style={{ marginInlineEnd: 0 }}>
                      {item.groupLabel}
                    </Tag>
                  ) : (
                    <Tag color="processing" style={{ marginInlineEnd: 0 }}>
                      Acción
                    </Tag>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
};

export default CommandPalette;
